// /sources — card per source, status badge, sync-now, view-runs link.
// Uses plain useState + useEffect (matches review-screen style; query layer
// stays optional per Phase-2a).

import { useCallback, useEffect, useState } from "react";
import { currentOrgSlug, listSources, syncNow } from "./api";
import { StatusBadge } from "./StatusBadge";
import type { SourceRecord } from "./types";
import "./sources.css";

interface Props {
  onAdd?(): void;
  onView?(id: string): void;
}

export function SourcesListPage({ onAdd, onView }: Props) {
  const [sources, setSources] = useState<SourceRecord[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  // `GET /sources` is org-scoped and always was — calling it without
  // `org_slug` was a 422, so this list could never have loaded. The org
  // comes from the session, not a prop: it is an authorization answer and
  // the server is the only thing entitled to give it.
  const refresh = useCallback(async () => {
    try {
      setSources(await listSources(await currentOrgSlug()));
    } catch (err) {
      setError(err as Error);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const onSync = async (id: string) => {
    setSyncing(id);
    try {
      await syncNow(id);
      await refresh();
    } finally {
      setSyncing(null);
    }
  };

  if (error) {
    return (
      <div className="maisight-sources-page" role="alert">
        Failed to load sources: {error.message}
      </div>
    );
  }
  if (sources === null) {
    return <div className="maisight-sources-page" role="status">Loading…</div>;
  }

  return (
    <div className="maisight-sources-page">
      <header className="maisight-sources-page-header">
        <h1>Sources</h1>
        {onAdd ? (
          <button
            type="button"
            className="maisight-sources-btn maisight-sources-btn--primary"
            onClick={onAdd}
          >
            Add a source
          </button>
        ) : null}
      </header>
      {sources.length === 0 ? (
        <p className="maisight-sources-empty">
          No sources yet. Connect a repo or an OpenAPI URL to start syncing docs.
        </p>
      ) : (
        <ul className="maisight-sources-cards" data-testid="sources-list">
          {sources.map((s) => (
            <li key={s.id} className="maisight-sources-card" data-source-id={s.id}>
              <div className="maisight-sources-card-main">
                <div className="maisight-sources-card-type">{s.type}</div>
                <div className="maisight-sources-card-name">{nameOf(s)}</div>
                <div className="maisight-sources-card-meta">
                  <StatusBadge status={s.last_sync_status} />
                  <span className="maisight-sources-card-sync">{relative(s.last_sync_at)}</span>
                </div>
              </div>
              <div className="maisight-sources-card-actions">
                <button
                  type="button"
                  className="maisight-sources-btn maisight-sources-btn--ghost"
                  disabled={syncing === s.id}
                  onClick={() => onSync(s.id)}
                  data-testid={`sync-now-${s.id}`}
                >
                  {syncing === s.id ? "Syncing…" : "Sync now"}
                </button>
                {onView ? (
                  <button
                    type="button"
                    className="maisight-sources-btn maisight-sources-btn--ghost"
                    onClick={() => onView(s.id)}
                  >
                    View runs
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function nameOf(s: SourceRecord): string {
  const c = s.config;
  if (typeof c.url === "string") return c.url;
  if (typeof c.repo === "string") return `${c.host ?? "github"}/${c.repo}`;
  return s.id;
}

function relative(iso: string | null): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const delta = Math.max(0, Date.now() - t);
  const mins = Math.round(delta / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
